<template>
  <div class="main-monitor">
    <div class="search-bar" id="searchInput">
      <input
        type="text"
        v-model="searchQuery"
        placeholder="搜索位置"
        id="tipinput"
        @keyup.enter="searchLocation"
      />
      <button @click="searchLocation">搜索</button>
      <button @click="clearSearch">取消</button>
    </div>
    <div class="main-container">
      <div class="monitor-map" ref="monitorMapRef"></div>
      <PrjList class="prjList" :projects="prjList" />
    </div>
    <!-- <div class="add-prj" :class="adding ? 'blueContainer' : ''" @click="addPrj">鏂板缓<br />椤圭洰</div> -->
    <Avatar></Avatar>
    <div class="addPrj-steps" v-show="showStep">
      <button class="close-btn" @click="closeSteps">x</button>
      <h3 class="addPrj-title">操作步骤</h3>
      <ol class="addPrj-list">
        <li>在地图中找到项目位置后单击鼠标左键。</li>
        <li>随后会打开新建项目窗口，填写项目信息。</li>
      </ol>
    </div>
    <el-dialog title="是否绘制项目范围" v-model="dialogVisible" width="30%" @close="handleClose">
      <span>请在地图上依次点击范围点位。当前版本首页地图仅支持展示，范围绘制后续再迁移。</span>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">跳过绘制</el-button>
          <el-button type="primary" @click="drawPolygon">开始绘制</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import ApiKeys from '@/config/ApiKeys.json'
import { ref, onMounted, createApp } from 'vue'
import { getPrjInfo, getCoor } from '../service/PrjInfo'
import HomePrjInfo from '@/components/mapWindow/HomePrjInfo.vue'
import AddProjectInfo from '@/components/mapWindow/AddProjectInfo.vue'
import PrjList from './PrjList.vue'
import Avatar from '../components/Avatar.vue'
import { ElDialog, ElMessage } from 'element-plus'
import Icon from '../assets/prjLocation.svg'

const monitorMapRef = ref(null)
const prjList = ref<any[]>([])
const coorList = ref<any[]>([])
const markerMap = new Map<number, any>()
const polygonOverlays = ref<any[]>([])

type MaybeRef<T> = T | null

let map: MaybeRef<any> = null
let infoWindow: MaybeRef<any> = null
let tempMarker: MaybeRef<any> = null
let TInstance: any = null

const adding = ref(false)
const showStep = ref(false)
const dialogVisible = ref(false)
const insertId = ref(0)
const latV = ref(0)
const lngV = ref(0)
const searchQuery = ref('')

const DEFAULT_CENTER = { lng: 121.47, lat: 31.23 }

const loadTiandituScript = () => {
  if ((window as any).T) {
    return Promise.resolve((window as any).T)
  }

  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>('script[data-tianditu="true"]')
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve((window as any).T), { once: true })
      existingScript.addEventListener('error', () => reject(new Error('Tianditu script load failed')), { once: true })
      return
    }

    const script = document.createElement('script')
    script.src = `https://api.tianditu.gov.cn/api?v=4.0&tk=${ApiKeys.apiKey}`
    script.async = true
    script.defer = true
    script.dataset.tianditu = 'true'
    script.onload = () => resolve((window as any).T)
    script.onerror = () => reject(new Error('Tianditu script load failed'))
    document.head.appendChild(script)
  })
}

const toLngLat = (lng: number, lat: number) => new TInstance.LngLat(Number(lng), Number(lat))

const getEventLngLat = (event: any) => {
  const point = event?.lnglat || event?.target?.getLngLat?.()
  if (!point) {
    return null
  }

  return {
    lng: Number(point.getLng ? point.getLng() : point.lng),
    lat: Number(point.getLat ? point.getLat() : point.lat)
  }
}

const createMarkerIcon = (image: string) =>
  new TInstance.Icon({
    iconUrl: image,
    iconSize: new TInstance.Point(45, 45),
    iconAnchor: new TInstance.Point(22, 42)
  })

const createMarker = (lng: number, lat: number, image = Icon) =>
  new TInstance.Marker(toLngLat(lng, lat), {
    icon: createMarkerIcon(image)
  })

const closeMapInfoWindow = () => {
  if (map?.closeInfoWindow) {
    map.closeInfoWindow()
  }
  infoWindow = null
}

const openInfoWindow = (content: HTMLElement, lng: number, lat: number) => {
  closeMapInfoWindow()
  infoWindow = new TInstance.InfoWindow(content, {
    offset: new TInstance.Point(0, -42)
  })
  map?.openInfoWindow(infoWindow, toLngLat(lng, lat))
}

const clearProjectMarkers = () => {
  markerMap.forEach((overlay) => {
    map?.removeOverLay?.(overlay)
  })
  markerMap.clear()
}

const clearPolygons = () => {
  polygonOverlays.value.forEach((overlay) => {
    map?.removeOverLay?.(overlay)
  })
  polygonOverlays.value = []
}

const renderProjectMarkers = () => {
  if (!map || !TInstance) {
    return
  }

  clearProjectMarkers()

  prjList.value.forEach((item: any) => {
    const statusMarker = createMarker(item.lng, item.lat)
    map.addOverLay(statusMarker)
    statusMarker.addEventListener('click', () => {
      const tempDiv = document.createElement('div')
      const app = createApp(HomePrjInfo, { prjInfo: item, deletePrj })
      app.mount(tempDiv)
      openInfoWindow(tempDiv, item.lng, item.lat)
    })
    markerMap.set(item.id, statusMarker)
  })
}

const renderPolygons = () => {
  if (!map || !TInstance) {
    return
  }

  clearPolygons()

  coorList.value.forEach((item: any) => {
    const path = (item.coordinates || [])
      .map((point: any) => {
        if (Array.isArray(point)) {
          return toLngLat(point[0], point[1])
        }
        if (point?.lng !== undefined && point?.lat !== undefined) {
          return toLngLat(point.lng, point.lat)
        }
        return null
      })
      .filter(Boolean)

    if (!path.length) {
      return
    }

    const polygon = new TInstance.Polygon(path, {
      color: '#FF33FF',
      weight: 2,
      opacity: 0.8,
      fillColor: '#1791fc',
      fillOpacity: 0.4
    })
    map.addOverLay(polygon)
    polygonOverlays.value.push(polygon)
  })
}

const showDialog = (id: number, lat: number, lng: number) => {
  insertId.value = id
  latV.value = lat
  lngV.value = lng
  dialogVisible.value = true
}

const handleClose = () => {
  console.log('dialog closed')
}

const clearSearch = () => {
  searchQuery.value = ''
}

const searchLocation = async () => {
  if (!searchQuery.value || !map) {
    clearSearch()
    return
  }

  try {
    const ds = encodeURIComponent(JSON.stringify({ keyWord: searchQuery.value }))
    const response = await fetch(`https://api.tianditu.gov.cn/geocoder?ds=${ds}&tk=${ApiKeys.apiKey}`)
    const result = await response.json()

    if (result?.status === '0' && result?.location) {
      const lng = Number(result.location.lon)
      const lat = Number(result.location.lat)
      map.centerAndZoom(toLngLat(lng, lat), 15)
      return
    }

    ElMessage.warning('未找到对应位置')
  } catch (error) {
    ElMessage.error('位置搜索失败')
  }
}

const deletePrj = (id: number) => {
  prjList.value = prjList.value.filter((item: any) => item.id !== id)
  if (markerMap.has(id)) {
    map?.removeOverLay?.(markerMap.get(id))
  }
  markerMap.delete(id)
  closeMapInfoWindow()
}

const imgMap: Record<number, string> = {
  1: 'https://image.surveyexcel.com/1200w_900h_50q_dfc87a13-fe85-4173-8eb5-4df98f2028bb',
  2: 'https://image.surveyexcel.com/1200w_900h_50q_255192be-a9f7-4855-a5e7-8263d2cacf66',
  3: 'https://image.surveyexcel.com/1200w_900h_50q_81e65ccd-2768-414c-9a34-da389b7a1bcf'
}

const newPrj = (event: any) => {
  const point = getEventLngLat(event)
  if (!point) {
    return
  }

  if (tempMarker) {
    map?.removeOverLay?.(tempMarker)
  }

  tempMarker = createMarker(point.lng, point.lat, imgMap[1])
  map?.addOverLay?.(tempMarker)

  const removeTempMarker = () => {
    closeMapInfoWindow()
    if (tempMarker) {
      map?.removeOverLay?.(tempMarker)
      tempMarker = null
    }
  }

  const addedPrj = (data: any) => {
    closeMapInfoWindow()
    map?.removeEventListener?.('click', newPrj)
    prjList.value.push(data)
    adding.value = false
    showStep.value = false
    renderProjectMarkers()
  }

  const tempDiv = document.createElement('div')
  const app = createApp(AddProjectInfo, {
    position: point,
    closeInfoWindow: removeTempMarker,
    addedPrj,
    drawPolygon,
    showDialog
  })
  app.mount(tempDiv)
  openInfoWindow(tempDiv, point.lng, point.lat)
}

const addPrj = () => {
  if (adding.value) {
    adding.value = false
    showStep.value = false
    map?.removeEventListener?.('click', newPrj)
  } else {
    map?.addEventListener?.('click', newPrj)
    adding.value = true
    showStep.value = true
  }
}

const closeSteps = () => {
  showStep.value = false
}

const drawPolygon = () => {
  dialogVisible.value = false
  ElMessage.info('首页地图已切换到天地图，范围绘制功能暂未迁移')
}

const initMap = async () => {
  TInstance = await loadTiandituScript()
  if (!monitorMapRef.value) {
    return
  }

  map = new TInstance.Map(monitorMapRef.value)
  map.centerAndZoom(toLngLat(DEFAULT_CENTER.lng, DEFAULT_CENTER.lat), 12)
  map.enableScrollWheelZoom()
  map.addEventListener('click', () => {
    closeMapInfoWindow()
  })

  renderProjectMarkers()
  renderPolygons()
}

onMounted(async () => {
  prjList.value = await getPrjInfo()
  coorList.value = await getCoor()
  await initMap()
})
</script>

<style scoped lang="scss">
.main-monitor {
  width: 100vw;
  height: calc(100vh - 10px);
  .search-bar {
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 999;
    display: flex;
    input {
      padding: 5px;
      width: 200px;
      border: 1px solid #ccc;
      border-radius: 4px;
    }
    button {
      padding: 5px 10px;
      margin-left: 10px;
      background-color: #508ef5;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
    }
  }

  .main-container {
    display: flex;
    // justify-content: center;
    .monitor-map {
      // width: 70vw;
      height: 100vh;
    }
    .prjList {
      flex: 1;
    }
  }

  .monitor-map {
    width: 100%;
    height: 100%;
  }
  .add-prj {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 90px;
    right: 90px;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background-color: rgb(255, 255, 255);
    font-size: 14px;
    line-height: 16px;
    border: 1px solid #ccc;
    vertical-align: middle;
  }
  .addPrj-text {
    width: 300px;
    height: 100px;
    background-color: #fff;
    border-radius: 5px;
  }
  .addPrj-steps {
    position: absolute;
    top: 90px;
    right: 155px;
    background: #f7f7f7;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    .close-btn {
      position: absolute;
      top: 10px;
      right: 10px;
      border: none;
      background: transparent;
      cursor: pointer;
      font-size: 24px;
      line-height: 1;
      color: #aaa;
      &:hover {
        color: #777;
      }
    }
    .addPrj-title {
      font-size: 18px;
      color: #333;
      margin-bottom: 10px;
    }
    .addPrj-list {
      list-style-type: none;
      counter-reset: step-counter;
      padding-left: 0;
      li {
        counter-increment: step-counter;
        font-size: 16px;
        line-height: 1.5;
        position: relative;
        padding-left: 30px;
        margin-bottom: 5px;
        &::before {
          content: counter(step-counter);
          position: absolute;
          left: 0;
          top: 0;
          background-color: #508ef5;
          color: white;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          text-align: center;
          line-height: 20px;
          font-weight: bold;
        }
      }
    }
  }
  .blueContainer {
    background-color: rgb(66, 192, 243);
  }
}
</style>
